import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification, useTranslate } from "@refinedev/core";

import {
  listIntegrationsQueryKey,
  listPlanMapOptionsQueryKey,
  useListIntegrationEndpointsQuery,
  useListPlanMapOptionsQuery,
} from "@modules/erpIntegrations/services/queries";
import {
  useCreatePlanMapOptionMutation,
  useDeletePlanMapOptionMutation,
  useEditPlanMapOptionMutation,
} from "@modules/erpIntegrations/services/mutations";
import { PlanMapOption } from "@modules/erpIntegrations/types";

import { PlanMapOptionsPanelProps } from "./types";

export function usePlanMapOptionsPanel({ integrationId }: PlanMapOptionsPanelProps) {
  const translate = useTranslate();
  const { open } = useNotification();
  const queryClient = useQueryClient();

  const [dialogOption, setDialogOption] = useState<PlanMapOption | "new" | null>(null);

  const optionsQuery = useListPlanMapOptionsQuery({ integration: integrationId });
  const endpointsQuery = useListIntegrationEndpointsQuery({ integration: integrationId });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: listPlanMapOptionsQueryKey,
      refetchType: "all",
    });
    // ConfigForm's "plan map option" select reads integration.plan_map_options,
    // which is embedded in the /v1/integration_admin/ list response cached
    // under listIntegrationsQueryKey (5min staleTime). Without this, a
    // freshly created/edited/deleted option won't show up there until that
    // cache naturally expires.
    queryClient.invalidateQueries({
      queryKey: listIntegrationsQueryKey,
      refetchType: "all",
    });
  };

  const createMutation = useCreatePlanMapOptionMutation({
    onSuccess: () => {
      invalidate();
      open?.({ type: "success", message: translate("erp_integrations.plan_map_options.create_success") });
      setDialogOption(null);
    },
  });

  const editMutation = useEditPlanMapOptionMutation({
    onSuccess: () => {
      invalidate();
      open?.({ type: "success", message: translate("erp_integrations.plan_map_options.edit_success") });
      setDialogOption(null);
    },
  });

  const deleteMutation = useDeletePlanMapOptionMutation({
    onSuccess: () => {
      invalidate();
      open?.({ type: "success", message: translate("erp_integrations.plan_map_options.delete_success") });
    },
  });

  const handleDelete = (option: PlanMapOption) => {
    if (!window.confirm(translate("erp_integrations.plan_map_options.confirm_delete"))) return;
    deleteMutation.mutate(option.id);
  };

  const handleSetDefault = (option: PlanMapOption) => {
    editMutation.mutate({ id: option.id, data: { is_default: true } });
  };

  const handleToggleActive = (option: PlanMapOption) => {
    editMutation.mutate({ id: option.id, data: { active: !option.active } });
  };

  return {
    translate,
    options: optionsQuery.data ?? [],
    isLoading: optionsQuery.isFetching,
    endpoints: endpointsQuery.data ?? [],
    dialogOption,
    openCreateDialog: () => setDialogOption("new"),
    openEditDialog: (option: PlanMapOption) => setDialogOption(option),
    closeDialog: () => setDialogOption(null),
    handleDelete,
    handleSetDefault,
    handleToggleActive,
    createMutation,
    editMutation,
    isMutating: createMutation.isPending || editMutation.isPending || deleteMutation.isPending,
  };
}
